import { useEffect, useState } from 'react'
import { Row, Col, Form } from 'react-bootstrap';
import { usePortalContext } from '../../lib/context/portalContext';
import { useDeviceMutations } from './deviceMutations';
import withAlerts from '../../lib/withAlerts';
import {compose} from '../../lib/compose';
import {useBulkOTACheck, useAnyOTACheck} from '../../lib/useOTACheck';
import FirmwareAssignmentConfirmModal from './firmwareAssignmentConfirmModal';
import { Link } from 'react-router-dom';

// Format a Date as "MMM DD, YYYY HH:mm:ss" in UTC — same as dateFormat utility
const formatUTC = (date) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month:    'short',
    day:      '2-digit',
    year:     'numeric',
    hour:     '2-digit',
    minute:   '2-digit',
    second:   '2-digit',
    hour12:   false,
  }).format(date);

// Add `hours` to a Date, zeroing out minutes/seconds/ms first
const addHours = (date, hours) => {
  const d = new Date(date);
  d.setUTCMinutes(0, 0, 0);
  d.setUTCHours(d.getUTCHours() + hours);
  return d;
};

// Add `days` to a Date and return ISO string — replaces moment().add(30, 'days')
const addDays = (isoString, days) => {
  const d = new Date(isoString);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
};

const files = {
  firmware: '/user_agreement/support/firmware_release_notes.pdf',
};

const BulkOtaDetails = props => {
  const [currentTime]         = useState(() => new Date().toISOString());
  const [startTime, setStartTime] = useState(currentTime);
  const { togglePortal } = usePortalContext();
  const { startBulkOTA } = useDeviceMutations();
  const {alerts} = props;
  const bulkOTARunning = useBulkOTACheck();
  const anyOTARunning = useAnyOTACheck();

  const handleFirmwareChange = async () => {
    const bulkOtaJob = await startBulkOTA({
      variables: {
        fw_ver:  'latest',
        start:   startTime,
        timeout: addDays(startTime, 30),
      },
    });

    if (bulkOtaJob.errors) {
      alerts.error({ title: 'Firmware Update', msg: 'There was an error updating firmware. Please try again.' });
    } else {
      alerts.success({ title: 'Firmware Update', msg: 'Update started. See log for details.' });
    }

    togglePortal();
  };

  // Build the 25 time options: "Now" + next 24 hours on the hour
  const timeOptions = [
    <option key={0} value={currentTime}>Now</option>,
    ...Array.from({ length: 24 }, (_, i) => {
      const d = addHours(new Date(currentTime), i + 1);
      return (
        <option key={i + 1} value={d.toISOString()}>
          {formatUTC(d)} UTC
        </option>
      );
    }),
  ];

  return (
    <div className="offcanvas-form" data-testid="offcanvas-bulk-ota">
      <Form className="mb-3">
        <Row className="mb-3">
          <Col>
            <p><strong>This action will replace all prior, outstanding OTA updates. All online devices will be updated to the latest firmware version.</strong></p>
            <p>To update to the latest firmware for all SafeCases please:</p>
            <ul className="font-no-underline">
              <li>
                Review the{' '}
                <a href={files.firmware} target="_blank" rel="noreferrer" className="font-no-underline">
                  SafeCase Firmware Release
                </a>{' '}
                notes
              </li>
              <li>Schedule the Start Time</li>
            </ul>
            <p>Note that this process has a daily operation window of 4 hours and will automatically expire in 30 days if incomplete.</p>
            <p>Please <Link to="/support" className="font-no-underline">contact support</Link> if you need further assistance.</p>

            <Form.Group>
              <Form.Label>Start Time</Form.Label>
              <Form.Select
                onChange={(e) => setStartTime(e.currentTarget.value)}
                disabled={bulkOTARunning || anyOTARunning}
              >
                {timeOptions}
              </Form.Select>
            </Form.Group>

            <Form.Group className="my-3" hidden={!bulkOTARunning}>
              <Form.Label className="noCapitalize">
                Firmware update to all SafeCases in progress, please wait for the update to complete.
              </Form.Label>
            </Form.Group>
            <Form.Group className="my-3" hidden={!anyOTARunning || bulkOTARunning}>
              <Form.Label className="noCapitalize">
                Firmware update in progress, please wait for the update to complete.
              </Form.Label>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group>
          <Row className="mb-3">
            <Col>
              <div className="d-grid gap-2">
                <FirmwareAssignmentConfirmModal
                  onChange={handleFirmwareChange}
                  scope="all"
                  disabledbutton={bulkOTARunning || anyOTARunning}
                />
              </div>
            </Col>
          </Row>
        </Form.Group>
      </Form>
    </div>
  );
};

export default compose(withAlerts)(BulkOtaDetails);
